// Sales Dashboard v2 — global multi-period compare control.
// One button + popover that owns the list of comparison ranges (max 10).
// Selected periods flow DOWN from the dashboard shell: the report tabs add
// $ / Δ% columns per period, the Overview trend overlays one line per period,
// KPI deltas re-anchor to the first period, and the per-tab Graph view plots
// every period as an aligned line. Quick generators:
//   · Previous period  — the adjacent window of equal length
//   · Same dates       — calendar-aligned, K years back (Jul 17 → Jul 17 LY)
//   · Same weekdays    — 364-day (52-week) steps, so a Thu–Fri range compares
//                        to the same Thu–Fri of prior years (dates shift)
//   · Whole month      — one click from the warehouse's real month span
//   · Custom           — From auto-fills To to match the current range length
import React, { useState } from 'react';
import {
  Box, Typography, Chip, Button, TextField, MenuItem, Select, Divider,
  Popover, Tooltip as MuiTooltip,
} from '@mui/material';

// distinct, color-blind-safe line/chip colors per comparison period (the
// current period keeps the theme primary)
export const PERIOD_COLORS = [
  '#E69F00', '#009E73', '#CC79A7', '#56B4E9', '#D55E00',
  '#8B7355', '#5B8FA6', '#A67B5B', '#999999', '#0072B2',
];

// ---- local-date helpers (never toISOString — UTC rollover shifts evenings ET)
export const pdate = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); };
export const pfmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const addDaysStr = (s, n) => { const d = pdate(s); d.setDate(d.getDate() + n); return pfmt(d); };
export const subYearsStr = (s, k) => {
  const d = pdate(s); const day = d.getDate();
  d.setDate(1); d.setFullYear(d.getFullYear() - k);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last)); return pfmt(d);
};
export const fmtShortRange = (from, to) => {
  const f = (x) => { const d = pdate(x); return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`; };
  return `${f(from)}–${f(to)}`;
};
const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return `${new Date(y, mo - 1, 1).toLocaleString('en-US', { month: 'short' })} '${String(y).slice(2)}`;
};

export default function CompareControl({ periods, onChange, range, months, dataMin }) {
  const [anchor, setAnchor] = useState(null);
  const [years, setYears] = useState(1);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const curLen = range?.from && range?.to
    ? Math.round((pdate(range.to) - pdate(range.from)) / 86400000) + 1 : 0;

  const addPeriods = (list) => {
    const seen = new Set(periods.map((p) => `${p.from}:${p.to}`));
    const next = [...periods];
    for (const p of list) {
      const key = `${p.from}:${p.to}`;
      if (!seen.has(key) && next.length < 10) { seen.add(key); next.push(p); }
    }
    onChange(next);
  };
  const addPrevPeriod = () => {
    if (!curLen) return;
    const to2 = addDaysStr(range.from, -1);
    addPeriods([{ from: addDaysStr(to2, -(curLen - 1)), to: to2, label: `Prev ${curLen}d` }]);
  };
  const addSameDates = (n) => {
    if (!curLen) return;
    addPeriods(Array.from({ length: n }, (_, i) => {
      const k = i + 1;
      return { from: subYearsStr(range.from, k), to: subYearsStr(range.to, k), label: `-${k}y dates` };
    }));
  };
  const addSameDays = (n) => {
    if (!curLen) return;
    addPeriods(Array.from({ length: n }, (_, i) => {
      const k = i + 1;
      return { from: addDaysStr(range.from, -364 * k), to: addDaysStr(range.to, -364 * k), label: `-${k}y days` };
    }));
  };
  const addMonthPeriod = (m) => {
    if (!m) return;
    const [y, mo] = m.split('-').map(Number);
    let to = `${m}-${String(new Date(y, mo, 0).getDate()).padStart(2, '0')}`;
    const today = pfmt(new Date());
    if (to > today) to = today;
    addPeriods([{ from: `${m}-01`, to, label: monthLabel(m) }]);
  };
  const onFromChange = (v) => {
    setCustomFrom(v);
    if (v && curLen > 0) setCustomTo(addDaysStr(v, curLen - 1));
  };
  const addCustomPeriod = () => {
    if (!customFrom || !customTo) return;
    const a = customFrom <= customTo ? customFrom : customTo;
    const b = customFrom <= customTo ? customTo : customFrom;
    addPeriods([{ from: a, to: b, label: fmtShortRange(a, b) }]);
    setCustomFrom(''); setCustomTo('');
  };

  return (
    <>
      <Chip
        label={periods.length ? `Compare: ${periods.length} period${periods.length > 1 ? 's' : ''}` : 'Compare Periods'}
        size="small" clickable
        color={periods.length ? 'primary' : 'default'}
        variant={periods.length ? 'filled' : 'outlined'}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ fontWeight: periods.length ? 700 : 400 }}
      />
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 340 } } }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5, mb: 0.25 }}>Compare periods</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1.5 }}>
          Current: {range?.from && range?.to ? fmtShortRange(range.from, range.to) : '—'} · applies to the whole dashboard — Overview, every tab, tables and graphs
        </Typography>

        {periods.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {periods.map((p, i) => {
              const partial = dataMin && p.from < dataMin;
              return (
                <MuiTooltip key={`${p.from}:${p.to}`} title={partial ? `Data starts ${dataMin} — this range is partly/fully before it` : `${p.from} → ${p.to}`}>
                  <Chip size="small"
                    label={`${p.label} · ${fmtShortRange(p.from, p.to)}`}
                    onDelete={() => onChange(periods.filter((_, j) => j !== i))}
                    sx={{
                      border: `2px solid ${partial ? '#B26A08' : PERIOD_COLORS[i % PERIOD_COLORS.length]}`,
                      bgcolor: 'transparent', fontWeight: 600,
                    }} />
                </MuiTooltip>
              );
            })}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Button size="small" variant="outlined" onClick={addPrevPeriod} disabled={periods.length >= 10}
            sx={{ textTransform: 'none', fontSize: 12 }}>+ Previous period</Button>
          <Box sx={{ flex: 1 }} />
          {periods.length > 0 && (
            <Button size="small" color="error" onClick={() => onChange([])} sx={{ textTransform: 'none', fontSize: 12 }}>Clear all</Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Select size="small" value={years} onChange={(e) => setYears(e.target.value)} sx={{ fontSize: 12, width: 110 }}>
            {[1, 2, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>{n} year{n > 1 ? 's' : ''} back</MenuItem>)}
          </Select>
          <Button size="small" variant="outlined" onClick={() => addSameDates(years)} disabled={periods.length >= 10}
            sx={{ textTransform: 'none', fontSize: 12 }}>+ Same dates</Button>
          <MuiTooltip title="364-day steps: a Thu–Fri range compares to the same Thu–Fri of prior years (weekday-aligned, dates shift)">
            <span>
              <Button size="small" variant="outlined" onClick={() => addSameDays(years)} disabled={periods.length >= 10}
                sx={{ textTransform: 'none', fontSize: 12 }}>+ Same weekdays</Button>
            </span>
          </MuiTooltip>
        </Box>

        <Divider sx={{ my: 1.25 }} />
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>Custom range</Typography>
        {(months || []).length > 0 && (
          <Select size="small" value="" displayEmpty fullWidth
            onChange={(e) => addMonthPeriod(e.target.value)}
            disabled={periods.length >= 10}
            renderValue={() => <span style={{ opacity: 0.65 }}>Add a whole month…</span>}
            sx={{ fontSize: 12, mb: 1 }}>
            {(months || []).slice(0, 26).map((m) => (
              <MenuItem key={m} value={m} sx={{ fontSize: 12.5 }}>{monthLabel(m)}</MenuItem>
            ))}
          </Select>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
            value={customFrom} onChange={(e) => onFromChange(e.target.value)}
            sx={{ '& input': { fontSize: 12 } }} />
          <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
            value={customTo} onChange={(e) => setCustomTo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustomPeriod(); }}
            inputProps={{ min: customFrom || undefined }} sx={{ '& input': { fontSize: 12 } }} />
          <Button size="small" variant="contained" onClick={addCustomPeriod}
            disabled={!customFrom || !customTo || periods.length >= 10} sx={{ textTransform: 'none', fontSize: 12 }}>Add</Button>
        </Box>
        {curLen > 0 && (
          <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5 }}>
            Picking From auto-fills To to match your current {curLen}-day range — edit To for a different span.
          </Typography>
        )}
        {dataMin && <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 1 }}>Warehouse data starts {dataMin} — older ranges show $0 / partial totals.</Typography>}
        {periods.length >= 10 && <Typography sx={{ fontSize: 10.5, color: 'warning.main', mt: 0.5 }}>Max 10 comparison periods.</Typography>}
      </Popover>
    </>
  );
}
